import { Alert, Collapse, LinearProgress, Paper, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

/**
 * POM-6010 — what the dialog shows while a commit runs, and afterwards.
 *
 * The commit is two writes the user cares about differently: saving the new NDC and lot, then
 * rebuilding the labels so the printed lot matches. Both are slow enough to look like a hang —
 * the Commit button's own spinner is in the title bar, far from where the user is looking — so
 * each stage says which one is running.
 *
 * The completion message is worth stating rather than just unlocking the buttons: the whole point
 * of the screen is that the label agrees with the lot, and the user cannot verify that by looking
 * at it. It is a fact rather than a hopeful guess because the dialog does not render it until the
 * rebuild is confirmed — by the per-label socket events, or by the status poll behind them.
 */
export const COMMIT_STAGE = {
  COMMITTING: 'COMMITTING',
  REGENERATING: 'REGENERATING',
  // A rebuild the user asked for with the Regenerate Label button, rather than one the commit
  // queued. Same bar and the same per-label counting — only the reason differs, so it gets its own
  // copy instead of telling the user their NDC selection is being applied when they changed nothing.
  REGENERATING_LABELS: 'REGENERATING_LABELS',
};

// Both rebuild labels one at a time and both count them the same way; only COMMITTING is a single
// opaque write with nothing to count.
const COUNTABLE_STAGES = [COMMIT_STAGE.REGENERATING, COMMIT_STAGE.REGENERATING_LABELS];

/**
 * How long the success line stays up.
 *
 * It is a receipt, not a status: it confirms something that already happened and needs no action,
 * so once it has been read it is a banner pushing the grid and the filters down the screen. Ten
 * seconds is long enough to read one sentence, and missing it costs nothing — the run succeeded.
 *
 * The failure line is NOT on a timer and must not be: it names labels that still show the old lot
 * and offers the retry, so it stays until the user deals with it.
 */
const SUCCESS_VISIBLE_MS = 10000;

const STAGE_COPY = {
  [COMMIT_STAGE.COMMITTING]: {
    title: 'Saving your changes…',
    detail: 'Writing the new NDC and lot to the prescription.',
  },
  [COMMIT_STAGE.REGENERATING]: {
    title: 'Regenerating labels…',
    detail: 'Labels are rebuilt now so the printed NDC and lot match what you selected.',
  },
  [COMMIT_STAGE.REGENERATING_LABELS]: {
    title: 'Regenerating labels…',
    detail: 'Rebuilding the selected labels. Download and Print unlock once they are ready.',
  },
};

const BulkLotNdcCommitProgress = ({ stage, regeneratedCount, completed, total }) => {
  const [expired, setExpired] = useState(false);

  /* Keyed on the stage as well as the count: a second commit runs through COMMITTING and back to
   * null, so this re-arms and gives that run its own ten seconds even when it happens to rebuild
   * the same number of labels as the one before it. */
  useEffect(() => {
    if (stage || regeneratedCount === 0) {
      setExpired(false);

      return undefined;
    }

    const timer = setTimeout(() => setExpired(true), SUCCESS_VISIBLE_MS);

    return () => clearTimeout(timer);
  }, [stage, regeneratedCount]);

  if (stage) {
    const { title, detail } = STAGE_COPY[stage];
    /* Determinate only while labels are genuinely ticking in. The rebuild arrives one label at a
       time over the socket, so a batch of 200 can show real movement instead of a bar that
       sweeps for two minutes; but if the socket is silent and the status poll is carrying the
       wait, `total` stays 0 and an indeterminate bar is the truthful thing to show. */
    const determinate = COUNTABLE_STAGES.includes(stage) && total > 0;

    return (
      <Paper variant='outlined' sx={{ mb: 2, p: 2 }}>
        <Typography variant='subtitle2'>{title}</Typography>
        <Typography variant='caption' color='text.secondary' component='div' sx={{ mb: 1.5 }}>
          {determinate ? `${detail} ${completed} of ${total} done.` : detail}
        </Typography>
        {/* Named, so it is not announced as a bare "progress bar" with no subject. */}
        <LinearProgress
          aria-label={title}
          variant={determinate ? 'determinate' : 'indeterminate'}
          value={determinate ? Math.round((completed / total) * 100) : undefined}
        />
      </Paper>
    );
  }

  if (regeneratedCount > 0) {
    return (
      // unmountOnExit so the collapsed line leaves no margin behind once it has gone.
      <Collapse in={!expired} unmountOnExit>
        <Alert severity='success' sx={{ mb: 2 }}>
          {`${regeneratedCount} label${
            regeneratedCount > 1 ? 's' : ''
          } rebuilt — the printed NDC and lot now match what you selected.`}
        </Alert>
      </Collapse>
    );
  }

  return null;
};

BulkLotNdcCommitProgress.propTypes = {
  stage: PropTypes.oneOf(Object.values(COMMIT_STAGE)),
  regeneratedCount: PropTypes.number,
  completed: PropTypes.number,
  total: PropTypes.number,
};

BulkLotNdcCommitProgress.defaultProps = {
  stage: null,
  regeneratedCount: 0,
  completed: 0,
  total: 0,
};

export default BulkLotNdcCommitProgress;
